import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Sparkles, Users, TrendingUp, Trophy,
  Coffee, Compass, ArrowRight, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { INQUIRY_INTERESTS } from "@/lib/inquiryOptions";

const HERO_IMG =
  "https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const EXPERIENCE = [
  "Not licensed yet",
  "Less than 1 year",
  "1–3 years",
  "3–7 years",
  "7+ years",
];

const INTERESTS = INQUIRY_INTERESTS;

const communityPillars = [
  {
    icon: Trophy,
    title: "Producers, not posers.",
    body: "The agents on this team close real business. You'll be sharpening up around people who have been through every market cycle and know what works.",
  },
  {
    icon: Users,
    title: "Mentorship without the noise.",
    body: "No top-down lectures. Real conversations with operators who will pick up your call, look at your numbers, and tell you the truth.",
  },
  {
    icon: Compass,
    title: "A culture you actually want to belong to.",
    body: "Steady, generous, and unflashy. The kind of team that shows up to each other's listings, kids' games, and big moments.",
  },
  {
    icon: Coffee,
    title: "The room you want to be in.",
    body: "Brilliant minds, big producers, and good humans — gathered around the same table, rooting for each other's wins.",
  },
];

const brokerageBenefits = [
  "Best-in-class KW models, systems, and tech (Command, MAPS, Win-Make-Give)",
  "Generous, transparent splits with profit-share for life",
  "Marketing, design, and back-office support so you can stay in production",
  "Local leadership that knows Maine and protects your time",
  "Access to Tammy's network — vendors, lenders, inspectors, and contractors she's trusted for 27 years",
];

export default function Recruiting() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", experience_years: "",
    current_brokerage: "", interest: "", message: "",
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fullMessage = form.interest
        ? `Interest: ${form.interest}\n\n${form.message}`
        : form.message;
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        experience_years: form.experience_years,
        current_brokerage: form.current_brokerage,
        message: fullMessage,
        inquiry_type: "recruiting",
        subject: "Recruiting Inquiry",
      });
      toast.success("Application received! Tammy will reach out personally.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="recruiting-success">
        <CheckCircle2 className="mx-auto text-primary" size={56} strokeWidth={1.4} />
        <h1 className="font-serif text-5xl mt-8">Thanks for reaching out.</h1>
        <p className="text-lg text-foreground/75 mt-6 leading-relaxed">
          Tammy will personally review your details and follow up by email
          within 3–5 business days to schedule a conversation.
        </p>
        <Link to="/">
          <Button variant="outline" className="mt-10 rounded-full px-8 h-12 border-border">
            Back to home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="recruiting-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Agents at the table" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 w-full">
          <span className="inline-block text-[11px] uppercase tracking-[0.32em] text-white/85 mb-6">
            Recruiting · Keller Williams Maine
          </span>
          <h1 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-4xl">
            Build alongside
            <br />
            <span className="italic text-[#93C5FD]" style={{ textShadow: "0 2px 24px rgba(147,197,253,0.35)" }}>producers who get it.</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-8 max-w-2xl leading-relaxed">
            Whether you're looking for serious coaching or simply a better
            home for your business — Tammy's team is small, intentional, and
            built for agents who want to grow without losing themselves.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <a href="#apply" data-testid="recruiting-hero-cta">
              <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
                Start the conversation <ArrowRight className="ml-2" size={18} />
              </Button>
            </a>
            <Link to="/producer-coaching" data-testid="recruiting-producer-link">
              <Button size="lg" variant="outline"
                className="rounded-full bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white backdrop-blur-md h-12 px-8 text-base">
                See producer coaching
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Two-path strip */}
      <section className="bg-muted/40 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-7">
              <span className="text-[11px] uppercase tracking-[0.3em] text-primary">Two doors. One team.</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                Pick the path that
                <br />
                <span className="italic text-primary">fits where you are.</span>
              </h2>
            </div>
            <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
              You don't have to be in coaching to join the brokerage. And
              you don't have to join the brokerage to be coached. Whatever
              you're after, the front door is the same conversation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Path 1 — Producer Coaching */}
            <div className="bg-card border border-border rounded-2xl p-8 card-lift flex flex-col" data-testid="path-coaching">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-primary">
                <Sparkles size={12} /> Path A
              </div>
              <h3 className="font-serif text-3xl mt-4 leading-tight">
                Performance-based coaching for producers.
              </h3>
              <p className="text-sm text-foreground/75 mt-3 leading-relaxed">
                Already producing? Tammy's signature offer: a coaching
                engagement where the fee is only paid once your production
                crosses an agreed threshold. No retainer. Real skin in the game.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/80">
                <li className="flex gap-2"><CheckCircle2 className="text-primary shrink-0 mt-0.5" size={14} /> No upfront fees</li>
                <li className="flex gap-2"><CheckCircle2 className="text-primary shrink-0 mt-0.5" size={14} /> Four coaching sessions a month</li>
                <li className="flex gap-2"><CheckCircle2 className="text-primary shrink-0 mt-0.5" size={14} /> Quarterly threshold reviews</li>
              </ul>
              <Link to="/producer-coaching" className="mt-7" data-testid="path-coaching-cta">
                <Button variant="outline" className="w-full rounded-full border-border h-11">
                  See the coaching offer <ArrowRight className="ml-2" size={14} />
                </Button>
              </Link>
            </div>

            {/* Path 2 — Brokerage */}
            <div className="bg-[#0F1E33] text-[#F5F8FB] rounded-2xl p-8 card-lift flex flex-col" data-testid="path-brokerage">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#93C5FD]">
                <BadgeCheck size={12} /> Path B
              </div>
              <h3 className="font-serif text-3xl mt-4 leading-tight">
                Just want a better home for your business?
              </h3>
              <p className="text-sm opacity-85 mt-3 leading-relaxed">
                Plenty of experienced agents join Tammy's team without ever
                being in coaching — they come for the community, the support,
                and the chance to work alongside people who are good at what
                they do and good to be around.
              </p>
              <ul className="mt-5 space-y-2 text-sm opacity-90">
                <li className="flex gap-2"><CheckCircle2 className="text-[#93C5FD] shrink-0 mt-0.5" size={14} /> No coaching required</li>
                <li className="flex gap-2"><CheckCircle2 className="text-[#93C5FD] shrink-0 mt-0.5" size={14} /> Competitive splits + profit share</li>
                <li className="flex gap-2"><CheckCircle2 className="text-[#93C5FD] shrink-0 mt-0.5" size={14} /> Marketing & ops support</li>
              </ul>
              <a href="#apply" className="mt-7" data-testid="path-brokerage-cta">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90 h-11">
                  Talk about joining <ArrowRight className="ml-2" size={14} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Room — community pitch */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">The Room</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              The kind of team
              <br />
              <span className="italic text-primary">you want to be on.</span>
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed text-lg">
            Talent attracts talent. The agents on Tammy's team are good at
            their work — and even better around a dinner table.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {communityPillars.map((p, i) => (
            <div key={p.title}
              className="bg-card border border-border rounded-2xl p-7 card-lift"
              data-testid={`community-pillar-${i}`}>
              <p.icon className="text-primary" size={26} strokeWidth={1.5} />
              <h3 className="font-serif text-xl mt-5 leading-tight">{p.title}</h3>
              <p className="text-sm text-foreground/70 mt-3 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brokerage benefits */}
      <section className="bg-muted/40 py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">Brokerage</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              What you get
              <br />
              <span className="italic text-primary">at Keller Williams Maine.</span>
            </h2>
            <p className="mt-6 text-foreground/75 leading-relaxed text-lg">
              The systems of a global brokerage with the soul of a small team.
            </p>
          </div>
          <ul className="md:col-span-7 grid gap-4">
            {brokerageBenefits.map((b) => (
              <li key={b} className="flex gap-4 bg-card border border-border rounded-xl p-5">
                <CheckCircle2 className="text-primary shrink-0 mt-1" size={18} strokeWidth={1.6} />
                <span className="text-foreground/85 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="max-w-6xl mx-auto px-6 md:px-10 py-24" data-testid="recruiting-apply">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">Start the conversation</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Reach out.
            </h2>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
              Tell Tammy a little about where you are and what you're after.
              Whether you're curious about coaching, the brokerage, or both —
              she'll personally read it and follow up.
            </p>
            <div className="mt-8 p-6 rounded-2xl bg-muted/60 border border-border">
              <div className="flex items-center gap-2 text-primary">
                <TrendingUp size={16} />
                <p className="text-xs uppercase tracking-[0.22em]">No pressure</p>
              </div>
              <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
                This isn't a sales call. It's a chance to compare notes with
                someone who has been in the business 27 years.
              </p>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="md:col-span-7 bg-card border border-border rounded-2xl p-8 md:p-10 space-y-5"
            data-testid="recruiting-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <Input value={form.name} onChange={update("name")} className="rounded-xl h-11"
                  data-testid="recruiting-name" required />
              </Field>
              <Field label="Email *">
                <Input type="email" value={form.email} onChange={update("email")} className="rounded-xl h-11"
                  data-testid="recruiting-email" required />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone">
                <Input value={form.phone} onChange={update("phone")} className="rounded-xl h-11"
                  data-testid="recruiting-phone" />
              </Field>
              <Field label="Years of experience">
                <Select value={form.experience_years} onValueChange={update("experience_years")}>
                  <SelectTrigger className="rounded-xl h-11" data-testid="recruiting-experience-trigger">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE.map((e) => (
                      <SelectItem key={e} value={e}
                        data-testid={`recruiting-experience-${e.replace(/[^a-zA-Z0-9]/g, '')}`}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Current brokerage (if any)">
              <Input value={form.current_brokerage} onChange={update("current_brokerage")} className="rounded-xl h-11"
                data-testid="recruiting-brokerage" placeholder="Optional" />
            </Field>
            <Field label="What are you most interested in?">
              <Select value={form.interest} onValueChange={update("interest")}>
                <SelectTrigger className="rounded-xl h-11" data-testid="recruiting-interest-trigger">
                  <SelectValue placeholder="Select one" />
                </SelectTrigger>
                <SelectContent>
                  {INTERESTS.map((i) => (
                    <SelectItem key={i} value={i}
                      data-testid={`recruiting-interest-${i.replace(/[^a-zA-Z0-9]/g, '')}`}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tell Tammy a bit about you *">
              <Textarea value={form.message} onChange={update("message")} rows={5} className="rounded-xl"
                data-testid="recruiting-message"
                placeholder="Where you are in your business, what you're hoping to figure out..."
                required />
            </Field>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="recruiting-submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 text-base"
            >
              {submitting ? "Submitting..." : "Send my application"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
